import { faPlus, faTrash } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SurveyQuestionResponse } from "@yardzen/api-dtos";
import { Button } from "@yardzen/ui/components/button";
import { Input } from "@yardzen/ui/components/input";
import { useState } from "react";
import { UseFormRegister } from "react-hook-form";
import { DynamicMultipleTextFieldsResponseType } from "../QuizQuestionHandler";
export default function DynamicMultipleTextFieldsResponse({
  fields,
  surveyQuestionResponse,
  register,
}: {
  fields: {
    key: string;
    label: string;
    placeholder?: string;
  }[];
  surveyQuestionResponse?: SurveyQuestionResponse;
  register: UseFormRegister<any>;
}) {
  let responses: DynamicMultipleTextFieldsResponseType = [];
  if (surveyQuestionResponse?.response) {
    try {
      const parsed = JSON.parse(surveyQuestionResponse.response);
      if (Array.isArray(parsed)) {
        responses = parsed;
      }
    } catch {
      responses = [];
    }
  }

  const getBaseKey = (key: string) => key.replace(/-\d+$/, "");

  const baseKey = fields[0] ? getBaseKey(fields[0].key) : "field";

  const [fieldList, setFieldList] = useState(() => {
    if (responses.length > 0) {
      return responses.map((res) => {
        return {
          key: res.key,
          label: fields[0]?.label || res.key,
          placeholder: fields[0]?.placeholder || "",
        };
      });
    }
    return [
      {
        key: fields[0]?.key || `${baseKey}-1`,
        label: fields[0]?.label || "Field 1",
        placeholder: fields[0]?.placeholder || "",
      },
    ];
  });

  const handleAddField = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setFieldList((prev) => {
      // Find all numbers used in keys
      const usedNumbers = prev
        .map((f) => {
          const match = f.key.match(/-(\d+)$/);
          return match ? parseInt(match[1], 10) : 0;
        })
        .filter(Boolean);

      // Find the next available number
      const nextIndex =
        usedNumbers.length > 0 ? Math.max(...usedNumbers) + 1 : 1;

      const lastLabel = fields[0]?.label || `Field ${nextIndex}`;
      return [
        ...prev,
        {
          key: `${baseKey}-${nextIndex}`,
          label: lastLabel,
          placeholder: fields[0]?.placeholder || "",
        },
      ];
    });
  };

  const handleRemoveField = (index: number) => {
    if (fieldList.length === 1) return;
    setFieldList((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="flex w-full flex-col flex-nowrap justify-center gap-4 px-4 sm:w-3/5 sm:px-0">
      {fieldList.map((field, index) => (
        <div key={field.key} className="relative">
          <label className="mb-2 block text-lg font-medium text-gray-700">
            {`${field.label} ${index + 1}`}
          </label>
          <div className="flex items-center gap-2">
            <Input
              maxLength={150}
              placeholder={field.placeholder}
              className="bg-brand-secondary-lighter w-full flex-1 rounded-md p-4"
              defaultValue={
                responses.find((res) => res.key === field.key)?.value || ""
              }
              {...register(`dynamicFields.${field.key}`)}
            />
            {fieldList.length > 1 && index > 0 ? (
              <Button
                onClick={() => handleRemoveField(index)}
                variant="destructive"
                endIcon={<FontAwesomeIcon icon={faTrash} />}
                size="icon"
                className="shrink-0"
              />
            ) : (
              <span className="inline-block h-10 w-10" />
            )}
          </div>
        </div>
      ))}
      <div className="mt-2 w-1/2">
        <Button
          onClick={handleAddField}
          variant="outline"
          startIcon={<FontAwesomeIcon icon={faPlus} />}
        >
          Add another
        </Button>
      </div>
    </div>
  );
}
